import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import getDaysByMonth from '../domain/utils/get-days-by-month';
import splitDaysOnWeeks from '../domain/utils/split-days-on-weeks';
import { Day } from '../domain/interfaces/day';
import { Calendar } from '../domain/interfaces/calendar';
import { createFetchTournamentDataReducer } from './redusers';
import { markGameDays } from './utils/mark-game-days';
import { markPassedDays } from './utils/mark-passed-days';
import { markToday } from './utils/mark-today';
import { markSelectedDay } from './utils/mark-selected-day';
import { clearSelectedDay } from './utils/clear-selected-day';
import { Championship } from '../domain/interfaces/championship';
import { markEmptyDays } from './utils/mark-empty-days';

export type ScheduleSliceState = {
  tournamentId?: number;

  isLoading: boolean;
  isLoaded: boolean;

  selectedDate: string;
  currentMonth: number;
  currentYear: number;
  weeks: Day[][];
  calendar: Calendar;

  championship?: Championship;

  championshipFilter: string;
};

const now = new Date();
const interval = getDaysByMonth({ month: now.getMonth(), year: now.getFullYear() });
const weeks = splitDaysOnWeeks({ interval });

const initialState: ScheduleSliceState = {
  isLoading: false,
  isLoaded: false,

  selectedDate: new Date().toISOString(),
  currentMonth: now.getMonth(),
  currentYear: now.getFullYear(),
  weeks: weeks,
  calendar: {},

  championshipFilter: 'all',
};

const slice = createSlice({
  name: 'schedule',
  initialState,
  extraReducers: (builder) => {
    createFetchTournamentDataReducer(builder);
  },
  reducers: {
    setCurrentDate: (state, action: PayloadAction<{ month: number; year?: number }>) => {
      const interval = getDaysByMonth(action.payload);
      state.weeks = splitDaysOnWeeks({ interval });
      state.currentMonth = action.payload.month;
      state.currentYear = action.payload.year ?? state.currentYear;
      markEmptyDays(state);
      markGameDays(state);
      markPassedDays(state);
      markToday(state);
      markSelectedDay(state);
    },
    setSelectedDate: (state, action: PayloadAction<string>) => {
      clearSelectedDay(state);
      state.selectedDate = action.payload;
      markSelectedDay(state);
    },
    setChampionshipFilter: (state, action: PayloadAction<string>) => {
      state.championshipFilter = action.payload;
    },
    setTournamentId: (state, action: PayloadAction<{ id: number }>) => {
      state.tournamentId = action.payload.id;
    },
  },
});

const scheduleReducer = slice.reducer;
export const { setCurrentDate, setSelectedDate, setChampionshipFilter, setTournamentId } = slice.actions;
export default scheduleReducer;
